import { useState, useEffect } from "react";
import { db } from "../../lib/firebase";
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { CheckCircle, XCircle, Clock, CreditCard, Smartphone, Building2, Search, Filter, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

interface Payment {
  id: string;
  userId: string;
  userEmail: string;
  courseId: string;
  courseName: string;
  amount: number;
  paymentMethod: "credit_card" | "bank_transfer" | "promptpay";
  status: "pending" | "completed" | "rejected";
  createdAt: any;
  slipUrl?: string;
}

export default function ApproveStudents() {
  const { user } = useAuthContext();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "completed" | "rejected">("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadPayments();
  }, [user]);

  async function loadPayments() {
    if (!user) return;

    try {
      setLoading(true);

      // Get instructor's courses
      const coursesQuery = query(
        collection(db, "courses"),
        where("instructorId", "==", user.uid)
      );
      const coursesSnap = await getDocs(coursesQuery);
      const courseIds = coursesSnap.docs.map(doc => doc.id);
      const coursesMap = new Map(
        coursesSnap.docs.map(doc => [doc.id, doc.data().title])
      );

      if (courseIds.length === 0) {
        setPayments([]);
        setLoading(false);
        return;
      }

      // Get payments for these courses
      const allPayments: Payment[] = [];
      for (let i = 0; i < courseIds.length; i += 10) {
        const batch = courseIds.slice(i, i + 10);
        const paymentsQuery = query(
          collection(db, "payments"),
          where("courseId", "in", batch)
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        
        paymentsSnap.docs.forEach(doc => {
          const data = doc.data();
          allPayments.push({
            id: doc.id,
            userId: data.userId,
            userEmail: data.userEmail || "ไม่ระบุ",
            courseId: data.courseId,
            courseName: coursesMap.get(data.courseId) || "ไม่ทราบชื่อคอร์ส",
            amount: data.amount,
            paymentMethod: data.paymentMethod,
            status: data.status,
            createdAt: data.createdAt,
            slipUrl: data.slipUrl
          });
        });
      }

      // Sort by date (newest first)
      allPayments.sort((a, b) => {
        const timeA = a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setPayments(allPayments);
    } catch (error) {
      console.error("Error loading payments:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(payment: Payment) {
    if (!user) return;

    try {
      setProcessing(true);

      // Update payment status
      await updateDoc(doc(db, "payments", payment.id), {
        status: "completed",
        approvedAt: serverTimestamp(),
        approvedBy: user.uid
      });

      // Add user to course enrollments
      await addDoc(collection(db, "enrollments"), {
        userId: payment.userId,
        courseId: payment.courseId,
        enrolledAt: serverTimestamp(),
        paymentId: payment.id,
        status: "active"
      });

      // Reload payments
      await loadPayments();
      alert("อนุมัติการชำระเงินสำเร็จ!");
    } catch (error) {
      console.error("Error approving payment:", error);
      alert("เกิดข้อผิดพลาดในการอนุมัติ");
    } finally {
      setProcessing(false);
    }
  }

  async function handleReject() {
    if (!selectedPayment || !rejectReason.trim()) {
      alert("กรุณาระบุเหตุผลในการยกเลิก");
      return;
    }

    try {
      setProcessing(true);

      // Update payment status
      await updateDoc(doc(db, "payments", selectedPayment.id), {
        status: "rejected",
        rejectedAt: serverTimestamp(),
        rejectedBy: user?.uid,
        rejectReason: rejectReason.trim()
      });

      // Reload payments
      await loadPayments();
      setShowRejectModal(false);
      setSelectedPayment(null);
      setRejectReason("");
      alert("ยกเลิกการชำระเงินสำเร็จ!");
    } catch (error) {
      console.error("Error rejecting payment:", error);
      alert("เกิดข้อผิดพลาดในการยกเลิก");
    } finally {
      setProcessing(false);
    }
  }

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case "credit_card":
        return <CreditCard className="w-5 h-5" />;
      case "promptpay":
        return <Smartphone className="w-5 h-5" />;
      case "bank_transfer":
        return <Building2 className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  const getPaymentMethodText = (method: string) => {
    switch (method) {
      case "credit_card":
        return "บัตรเครดิต";
      case "promptpay":
        return "พร้อมเพย์";
      case "bank_transfer":
        return "โอนเงิน";
      default:
        return method;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-700">
            <Clock className="w-4 h-4" />
            รอตรวจสอบ
          </span>
        );
      case "completed":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
            <CheckCircle className="w-4 h-4" />
            อนุมัติแล้ว
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
            <XCircle className="w-4 h-4" />
            ยกเลิกแล้ว
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "ไม่ทราบวันที่";
    const date = timestamp.toDate?.() || new Date(timestamp);
    return new Intl.DateTimeFormat("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };

  const filteredPayments = payments
    .filter(p => filter === "all" || p.status === filter)
    .filter(p => 
      searchTerm === "" || 
      p.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const pendingCount = payments.filter(p => p.status === "pending").length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลดข้อมูล...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20">
      {/* Back Button */}
      <Link to="/instructor/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
        <ArrowLeft className="w-5 h-5" />
        กลับไปหน้า Dashboard
      </Link>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-pink-600">
              อนุมัตินักเรียน
            </h1>
            <p className="text-gray-600 mt-2">ตรวจสอบและอนุมัติการชำระเงินของนักเรียน</p>
          </div>
          {pendingCount > 0 && (
            <div className="bg-gradient-to-r from-yellow-400 to-blue-500 text-white px-6 py-3 rounded-2xl shadow-lg">
              <p className="text-sm font-medium">รอตรวจสอบ</p>
              <p className="text-3xl font-black">{pendingCount}</p>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="ค้นหาชื่อคอร์สหรืออีเมลนักเรียน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-purple-100 focus:outline-none transition-all"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                filter === "all"
                  ? "bg-gradient-to-r from-blue-600 to-pink-600 text-white shadow-lg"
                  : "bg-white text-gray-600 border-2 border-gray-200 hover:border-blue-300"
              }`}
            >
              ทั้งหมด ({payments.length})
            </button>
            <button
              onClick={() => setFilter("pending")}
              className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                filter === "pending"
                  ? "bg-gradient-to-r from-yellow-400 to-blue-500 text-white shadow-lg"
                  : "bg-white text-gray-600 border-2 border-gray-200 hover:border-yellow-300"
              }`}
            >
              รอตรวจสอบ ({pendingCount})
            </button>
          </div>
        </div>
      </div>

      {/* Payments List */}
      {filteredPayments.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">ไม่มีรายการ</h3>
          <p className="text-gray-500">
            {filter === "pending" 
              ? "ไม่มีรายการรอตรวจสอบในขณะนี้" 
              : "ไม่พบรายการที่ค้นหา"}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPayments.map((payment) => (
            <div
              key={payment.id}
              className="bg-white rounded-3xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300 border-2 border-gray-100"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                {/* Left Section */}
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      {getPaymentMethodIcon(payment.paymentMethod)}
                      <span className="text-white font-bold text-sm"></span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{payment.courseName}</h3>
                        {getStatusBadge(payment.status)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <p className="text-gray-600">
                          <span className="font-semibold">อีเมล:</span> {payment.userEmail}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-semibold">ยอดชำระ:</span>{" "}
                          <span className="text-green-600 font-bold">฿{payment.amount.toLocaleString()}</span>
                        </p>
                        <p className="text-gray-600">
                          <span className="font-semibold">วิธีชำระ:</span> {getPaymentMethodText(payment.paymentMethod)}
                        </p>
                        <p className="text-gray-600">
                          <span className="font-semibold">วันที่:</span> {formatDate(payment.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Section - Actions */}
                {payment.status === "pending" && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApprove(payment)}
                      disabled={processing}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      <CheckCircle className="w-5 h-5" />
                      อนุมัติ
                    </button>
                    <button
                      onClick={() => {
                        setSelectedPayment(payment);
                        setShowRejectModal(true);
                      }}
                      disabled={processing}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                    >
                      <XCircle className="w-5 h-5" />
                      ยกเลิก
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">ยกเลิกการชำระเงิน</h3>
            <p className="text-gray-600 mb-4">กรุณาระบุเหตุผลในการยกเลิก</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="เช่น: สลิปโอนเงินไม่ถูกต้อง, ยอดเงินไม่ตรง..."
              rows={4}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-100 focus:outline-none transition-all resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setSelectedPayment(null);
                  setRejectReason("");
                }}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleReject}
                disabled={processing || !rejectReason.trim()}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
              >
                ยืนยันยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
